import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

// Icons
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import EditIcon from "@mui/icons-material/Edit";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import PaidIcon from "@mui/icons-material/Paid";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";

import BaseBubble from "./BaseBubble";
import { getFileType, isMessageMine } from "../../Utils/constants";
import { styled } from "@mui/material/styles";

/* ───────────────────────── helpers ───────────────────────── */

const AddressLine = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.25),
  color: theme.palette.text.secondary,
  fontSize: "0.9rem",
}));

const GetIcon = (attachmentType, fileData) => {
  switch (attachmentType) {
    case "invoice":
      return <ReceiptLongIcon fontSize="small" />;
    case "file":
      return fileData?.content_type?.startsWith("image/")
        ? <ImageOutlinedIcon fontSize="small" />
        : <InsertDriveFileOutlinedIcon fontSize="small" />;
    case "campaign":
      return <CampaignOutlinedIcon fontSize="small" />;
    case "roster":
      return <GroupOutlinedIcon fontSize="small" />;
    case "mediakit":
      return <DescriptionOutlinedIcon fontSize="small" />;
    case "creator":
      return <PersonOutlinedIcon fontSize="small" />;
    case "address":
      return <LocalShippingIcon fontSize="small" />;
    case "campaign_video":
      return <MovieCreationIcon fontSize="small" />;
    default:
      return <InsertDriveFileOutlinedIcon fontSize="small" />;
  }
};

const isImageFile = (fileData) =>
  !!fileData && getFileType(fileData.file_url) === "image";

/** Check whether the video (incl. all cross‑posts) is ready for payment. */
const paymentReady = (videoWithCross) =>
  !!videoWithCross.liveLink &&
  !!videoWithCross.boostCode &&
  videoWithCross.crossposts.every(
    (c) => !!c.liveLink && !!c.boostCode
  ) &&
  !videoWithCross.payoutComplete;

/* ───────────────────────── attachment renderer ───────────────────────── */

function RenderAttachment({
  att,
  convMeta,
  onEditFile,
  onVideoLink,
  isUser,
  onCompletePayment,
  onRequestPayment,
  completePaymentLoading,
  requestPaymentLoading,
}) {
  const attachmentType = att.attachment_type;
  const fileData = att.file;

  /* generic loading guard for most types */
  if (
    attachmentType !== "invoice" &&
    !att.file &&
    !att.creator &&
    !att.roster &&
    !att.campaign &&
    !att.data
  ) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading attachment...</Typography>
      </Box>
    );
  }

  /* 1) Invoice */
  if (attachmentType === "invoice") {
    const invoice = att.data?.invoice_data;
    if (!invoice)
      return <Typography color="error">Invalid invoice data.</Typography>;

    return (
      <Card variant="outlined" sx={{ maxWidth: 320 }}>
        <CardHeader
          avatar={<ReceiptLongIcon color="primary" />}
          title={`Invoice #${invoice.id ?? "----"}`}
          subheader={`PO ${invoice.po_number ?? "N/A"}`}
        />
        <Divider />
        <CardContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Amount: <strong>${invoice.amount}</strong>
          </Typography>
          <Typography variant="body2">
            Client: <strong>{invoice.client_name}</strong>
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.85rem", opacity: 0.8, mt: 1 }}
          >
            Status: {invoice.invoice_status ?? "pending"}
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Button
            size="small"
            component={RouterLink}
            to={`/creatorinvoice/${invoice.id}`}
            variant="outlined"
          >
            View
          </Button>
          <Tooltip title="Share invoice">
            <IconButton
              component={RouterLink}
              to={`/creatorinvoice/${invoice.id}`}
            >
              <ShareOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </CardActions>
      </Card>
    );
  }

  /* 2) File */
  if (attachmentType === "file") {
    if (!fileData)
      return <Typography variant="body2">No file data found.</Typography>;

    if (isImageFile(fileData)) {
      return (
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <Link
            href={fileData.file_url}
            download={fileData.filename}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Box
              component="img"
              src={fileData.file_url}
              alt="Attachment"
              sx={{
                maxWidth: 200,
                maxHeight: 200,
                borderRadius: 1,
                cursor: "pointer",
                transition: ".3s",
                "&:hover": { boxShadow: 3, transform: "scale(1.02)" },
              }}
            />
          </Link>

          {!!fileData.id && (
            <Tooltip title="Edit file">
              <IconButton
                onClick={() => onEditFile?.(fileData)}
                sx={{ position: "absolute", top: 4, right: 4, bgcolor: "white" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    }

    /* non‑image */
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton component={RouterLink} to={`/files/${fileData.id}`} color="primary">
          <ShareOutlinedIcon />
        </IconButton>
        <Typography variant="body2">{fileData.filename}</Typography>
        <Chip
          label={(fileData.content_type?.split("/")[1] ?? "file").toUpperCase()}
          size="small"
          sx={{ ml: 1 }}
          color="secondary"
        />
        {!!fileData.id && (
          <Tooltip title="Edit file">
            <IconButton onClick={() => onEditFile?.(fileData)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  /* 3) Campaign */
  if (attachmentType === "campaign") {
    return (
      <Link
        component={RouterLink}
        to={`/campaigns/${att.campaign?.id ?? ""}`}
        style={{ textDecoration: "none", color: "inherit", display: "inline-block" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            cursor: "pointer",
            transition: ".2s",
            "&:hover": { opacity: .8, transform: "translateX(2px)" },
          }}
        >
          <CampaignOutlinedIcon color="primary" />
          <Typography variant="body2">
            {att.campaign?.name ?? "Unnamed Campaign"}
          </Typography>
          <Chip label="Campaign" size="small" color="secondary" />
        </Box>
      </Link>
    );
  }

  /* 4) Creator */
  if (attachmentType === "creator") {
    return (
      <Link
        component={RouterLink}
        to={`/creators/${att.creator?.creator ?? ""}`}
        style={{ textDecoration: "none", color: "inherit", display: "inline-block" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            cursor: "pointer",
            transition: ".2s",
            "&:hover": { opacity: .8, transform: "translateX(2px)" },
          }}
        >
          <Avatar src={att.creator?.pfphref} sx={{ width: 24, height: 24 }} />
          <Typography variant="body2">
            {att.creator?.creator ?? "Unnamed Creator"}
          </Typography>
          <Chip label="Creator" size="small" color="secondary" />
        </Box>
      </Link>
    );
  }

  /* 5) Address */
  if (attachmentType === "address") {
    const addressData = att.data;
    if (!addressData)
      return <Typography color="error">Invalid address data.</Typography>;

    return (
      <Box>
        <CardHeader avatar={<LocalShippingIcon />} title="Delivery Address" />
        <Divider />
        <CardContent>
          <Typography variant="body1" fontWeight={600}>
            {addressData.recipient}
          </Typography>
          <AddressLine variant="body2">{addressData.address}</AddressLine>
          <AddressLine variant="body2">
            {addressData.city}, {addressData.state} {addressData.zip}
          </AddressLine>
          <AddressLine variant="body2">{addressData.country}</AddressLine>
          {addressData.instructions && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {addressData.instructions}
            </Alert>
          )}
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PhoneIcon color="primary" sx={{ fontSize: "0.9rem", mr: 0.5 }} />
            <Typography variant="body2">{addressData.phone}</Typography>
          </Box>
        </CardActions>
      </Box>
    );
  }

  /* 6) Campaign video – **NOW WITH PAYMENT BUTTONS** */
  if (attachmentType === "campaign_video") {
    const videoData = att.data;
    if (!videoData?.video)
      return (<Box p={2}><Typography color="error">Video data not found.</Typography></Box>);

    const video = {
      ...videoData.video,
      crossposts: videoData.crossposts ?? [],
    };
    const eligible = paymentReady(video);

    return (
      <Box>
        <CardHeader
          avatar={<MovieCreationIcon />}
          title="Campaign Video"
          subheader={`#${String(video.id).padStart(3, "0")}`}
        />
        <Divider />

        {/* main content */}
        <CardContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              columnGap: 2,
              rowGap: .5,
              maxWidth: "100%",
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">Platform</Typography>
            <Chip label={`${video.promotionPlatform}`} size="small" />

            <Typography variant="subtitle2" color="text.secondary">Type</Typography>
            <Chip label={`${video.promotionType}`} size="small" />

            <Typography variant="subtitle2" color="text.secondary">Link</Typography>
            {video.liveLink
              ? <Link href={video.liveLink} target="_blank">Video</Link>
              : <i>Unavailable</i>}
          </Box>

          {/* cross‑posts */}
          {video.crossposts.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Cross‑posts
              </Typography>
              {video.crossposts.map((c) => (
                <Chip
                  key={c.id}
                  label={`${c.promotionPlatform}`}
                  size="small"
                  sx={{ mr: .5, mb: .5 }}
                  color="secondary"
                />
              ))}
            </Box>
          )}
        </CardContent>

        <Divider />
        <CardActions sx={{ justifyContent: "space-between", px: 2 }}>
          <Button onClick={() => onVideoLink(video.id)}>Details</Button>

          {isUser ? (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<PaidIcon />}
              disabled={!eligible || completePaymentLoading}
              onClick={() => onCompletePayment?.(video.id)}
            >
              {completePaymentLoading ? "Completing…" : "Payment Ready"}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<RequestQuoteIcon />}
              disabled={!eligible || requestPaymentLoading}
              onClick={() => onRequestPayment?.(video.id)}
            >
              {requestPaymentLoading ? "Sending…" : "Payment Pending"}
            </Button>
          )}
        </CardActions>
      </Box>
    );
  }

  /* fallback */
  return (
    <Typography variant="body2" color="text.secondary">
      Unknown attachment type: {attachmentType}
    </Typography>
  );
}

/* ───────────────────────── main wrapper ───────────────────────── */

export default function AttachmentBubble({
  msg,
  userId,
  isTcc,
  isUser,                    /* ← NEW */
  metadata,
  handleDeleteMessage,
  deleteMessageLoading,
  onEditFile,
  handleVideoLink,
  onCompletePayment,         /* ← NEW */
  onRequestPayment,          /* ← NEW */
  completePaymentLoading,    /* ← NEW */
  requestPaymentLoading,     /* ← NEW */
}) {
  const isUserMessage = isMessageMine(userId, msg);
  const att = msg.attachments;
  if (!att) return null;

  const attachmentType = att.attachment_type ?? "unknown";

  return (
    <Tooltip
      title={`${attachmentType.charAt(0).toUpperCase() + attachmentType.slice(1)} attachment`}
    >
      <BaseBubble
        isUserMessage={isUserMessage}
        isTcc={isTcc}
        handleDeleteMessage={() => handleDeleteMessage(msg.id)}
        deleteMessageLoading={deleteMessageLoading}
        customColors
        padding={["address", "campaign_video"].includes(attachmentType) ? 0 : 2}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <RenderAttachment
            att={att}
            convMeta={metadata}
            onEditFile={onEditFile}
            onVideoLink={handleVideoLink}
            isUser={isUser}
            onCompletePayment={onCompletePayment}
            onRequestPayment={onRequestPayment}
            completePaymentLoading={completePaymentLoading}
            requestPaymentLoading={requestPaymentLoading}
          />
        </Box>
      </BaseBubble>
    </Tooltip>
  );
}
